<script setup>
import { defineProps, defineExpose, ref, computed, getCurrentInstance } from 'vue';
import UtilObject from '@/utils/UtilObject';
import UtilNumber from '@/utils/UtilNumber';
import ModalFullscreen from '@/components/v2/modais/ModalFullscreen/ModalFullscreen.vue';
import BotaoTextoBase from '@/components/v2/base/BotaoTextoBase/BotaoTextoBase.vue';
import ConteudoModal from './ConteudoModal.vue';
import CartaoOSFilhas from '../CartaoOSFilhas.vue';
import ModalItemOS from './ModalItemOS.vue';
import OrdemDeServicoService from '@/api/v2/services/OrdemDeServicoService.js';
import ERP from '@/api/v2/ERP.mjs';
import Schema from '@/api/v2/Schema.mjs';
import EntityService from '@/services/entityService';
import useUsuarioStore from '@/api/v2/stores/UsuarioStore';

const props = defineProps({
    disabled: { type: Boolean, default: false },
    jobs: { type: Array, default: () => [] },
    coordenadores: { type: Array, default: () => [] },
    ordensDeServico: { type: Array, default: () => [] },
    onClose: { type: Function, default: null }
});

const modalFullscreenRef = ref();
const conteudoModalRef = ref(null);
const dado = ref({});
const historico = ref([]);
const itensDaOS = ref([]);
const osFilhas = ref([]);
const modalItemOS = ref(false);
const itemSelecionado = ref(null);
const usuarioStore = useUsuarioStore();
const vm = getCurrentInstance()?.proxy;

const dialogAcaoVisivel = ref(false);
const dialogAcaoTitulo = ref('');
const dialogAcaoMensagem = ref('');
const dialogAcaoBotaoConfirmar = ref('Confirmar');
const dialogAcaoExecutando = ref(false);
const dialogAcaoCallback = ref(null);

const pegaId = (value) => {
    if (value && typeof value === 'object') return value.id || value._id || value.userId || value.valueId || '';
    return value || '';
};

const extrairPrimeiroId = (...candidatos) => {
    const visitar = (valor) => {
        if (valor === undefined || valor === null || valor === '') return '';

        if (Array.isArray(valor)) {
            for (const item of valor) {
                const idItem = visitar(item);
                if (idItem) return idItem;
            }
            return '';
        }

        if (typeof valor === 'object') {
            return visitar(
                valor?.id
                || valor?._id
                || valor?.valueId
                || valor?.solicitacaoDeOrcamento
                || valor?.solicitacaoDeOrcamentoId
                || valor?.salesOrder
                || valor?.salesOrderId
                || ''
            );
        }

        const texto = String(valor || '').trim();
        if (!texto) return '';
        if (texto.includes(',')) {
            return String(texto.split(',').map((p) => p.trim()).find(Boolean) || '');
        }
        return texto;
    };

    for (const candidato of candidatos) {
        const id = visitar(candidato);
        if (id) return id;
    }

    return '';
};

const primeiroValor = (...valores) => {
    for (const valor of valores) {
        if (valor !== undefined && valor !== null && valor !== '') return valor;
    }
    return '';
};

const ehIdPersistidoLocal = (id = '') => {
    const texto = String(id || '').trim();
    if (!texto) return false;
    if (texto.startsWith('tmp-') || texto.startsWith('os-item-')) return false;
    return texto.length >= 8;
};

const normalizaDataParaInput = (valor = '', fallback = '') => {
    const texto = String(valor || '').trim();
    if (!texto) return fallback;

    if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
        return texto;
    }

    const matchISO = texto.match(/^(\d{4}-\d{2}-\d{2})/);
    if (matchISO?.[1]) {
        return matchISO[1];
    }

    const dataParseada = new Date(texto);
    if (!Number.isNaN(dataParseada.getTime())) {
        return dataParseada.toISOString().slice(0, 10);
    }

    return fallback;
};

const normalizaNome = (value = '') => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const normalizaTextoComparavel = (value = '') => normalizaNome(String(value || '').replace(/\s+/g, ' '));

const isTipoFabricacao = (tipo = '') => normalizaNome(tipo) === 'fabricacao';

const extraiNomeCoordenadorOS = (registro = {}) => {
    return String(
        primeiroValor(
            registro?.coordenadorDeProjetosNome,
            registro?.coordenadorDeProjetos?.name,
            registro?.coordenadorDeProjetos?.nome,
            registro?.coordenadorDeProjetos?.description,
            ''
        )
    ).trim();
};

const extraiNomeCoordenadorContratoJob = (registro = {}) => {
    const job = registro?.job && typeof registro.job === 'object' ? registro.job : {};

    // Tenta extrair do job completo (object) ou de campos de desnormalização na própria OS
    return String(
        primeiroValor(
            registro?.jobCoordenadorDeContratoNome,
            registro?.jobCoordenadorContratosNome,
            job?.coordenadorDeContratos?.nome,
            job?.coordenadorDeContratos?.name,
            job?.coordenadorDeContratosNome,
            job?.coordenadorDeContrato,
            job?.coordenadorContrato,
            job?.coordenadorContratoNome,
            job?.contractCoordinatorName,
            job?.contractCoordinator?.name,
            job?.contractCoordinator?.nome,
            job?.contractCoordinator,
            ''
        )
    ).trim();
};

const normalizaItemDaOS = (item = {}, index = 0) => {
    const itemRef = item?.item && typeof item.item === 'object' ? item.item : {};
    const unidadeRef = item?.unidade && typeof item.unidade === 'object' ? item.unidade : {};
    const unidadeCompraRef = item?.unidadeCompra && typeof item.unidadeCompra === 'object' ? item.unidadeCompra : {};

    const quantidade = UtilNumber.toNumber(primeiroValor(item?.quantidade, item?.qtd, item?.quantidadeSolicitada, 0));
    const valorUnitario = UtilNumber.toNumber(primeiroValor(item?.valorUnitario, item?.valorUnit, item?.precoUnitario, 0));
    const valorTotal = UtilNumber.toNumber(primeiroValor(item?.valorTotal, item?.precoTotal, quantidade * valorUnitario, 0));
    const desenhoNumero = String(primeiroValor(
        item?.['Numero do Desenho'],
        item?.numeroDoDesenho,
        item?.numeroDesenho,
        item?.numero_desenho,
        item?.drawingNumber,
        ''
    ) || '');
    const desenhoPosicao = String(primeiroValor(
        item?.['Posição do Item no Desenho'],
        item?.['Posicao do Item no Desenho'],
        item?.posicaoDoItemNoDesenho,
        item?.posicaoItemNoDesenho,
        item?.posicaoNoDesenho,
        item?.posicao_do_item_no_desenho,
        item?.drawingPosition,
        ''
    ) || '');
    const desenhoMontagemResolvido = String(primeiroValor(
        item?.desenhoMontagem,
        item?.desenhoDeMontagem,
        item?.desenho_montagem,
        (desenhoNumero && desenhoPosicao) ? `${desenhoNumero}/${desenhoPosicao}` : desenhoNumero,
        ''
    ));

    return {
        ...item,
        id: primeiroValor(item?.id, item?._id, `os-item-${index}`),
        _id: primeiroValor(item?._id, item?.id, `os-item-${index}`),
        sequencia: String(primeiroValor(item?.sequencia, item?.sequence, item?.codigoSequencia, '') || ''),
        item: primeiroValor(pegaId(item?.item), item?.itemId, item?.item || ''),
        itemCodigo: String(primeiroValor(item?.itemCodigo, item?.codigoItem, itemRef?.code, itemRef?.codigo, '')),
        itemCodigoExt: String(primeiroValor(item?.itemCodigoExt, item?.itemCodigo, item?.codigoItem, itemRef?.codeExt, itemRef?.code, itemRef?.codigo, '')),
        descricao: String(primeiroValor(item?.descricao, item?.description, item?.itemDescricao, itemRef?.description, itemRef?.descricao, '')),
        status: String(primeiroValor(item?.status, item?.situacao, '')),
        destino: String(primeiroValor(item?.destino, item?.destination, '')),
        unidade: primeiroValor(pegaId(item?.unidade), item?.unidadeId, item?.unidade || ''),
        unidadeSigla: String(primeiroValor(item?.unidadeSigla, item?.siglaUnidade, unidadeRef?.sigla, unidadeRef?.code, '')),
        unidadeCompra: primeiroValor(pegaId(item?.unidadeCompra), item?.unidadeCompraId, item?.unidadeCompra || ''),
        unidadeCompraSigla: String(primeiroValor(item?.unidadeCompraSigla, item?.siglaUnidadeCompra, unidadeCompraRef?.sigla, unidadeCompraRef?.code, '')),
        quantidade,
        quantidadeCompra: UtilNumber.toNumber(primeiroValor(item?.quantidadeCompra, item?.qtdComprada, item?.quantidadeComprada, 0)),
        quantidadeParaEntregar: UtilNumber.toNumber(primeiroValor(item?.quantidadeParaEntregar, item?.qtdParaEntregar, 0)),
        quantidadeEntregue: UtilNumber.toNumber(primeiroValor(item?.quantidadeEntregue, item?.qtdEntregue, 0)),
        quantidadeParaEmbarcar: UtilNumber.toNumber(primeiroValor(item?.quantidadeParaEmbarcar, item?.qtdParaEmbarcar, 0)),
        quantidadeEmbarcada: UtilNumber.toNumber(primeiroValor(item?.quantidadeEmbarcada, item?.qtdEmbarcada, 0)),
        pesoTotal: UtilNumber.toNumber(primeiroValor(item?.pesoTotal, item?.peso, 0)),
        valorUnitario,
        valorTotal,
        dataSolicitacao: normalizaDataParaInput(primeiroValor(item?.dataSolicitacao, item?.createdAt, '')),
        dataNecessidade: normalizaDataParaInput(primeiroValor(item?.dataNecessidade, item?.dataPrevisao, '')),
        dataEntrega: normalizaDataParaInput(primeiroValor(item?.dataEntrega, item?.deliveryDate, '')),
        desenhoFabricacao: String(primeiroValor(item?.desenhoFabricacao, item?.desenhoDeFabricacao, item?.desenho_fabricacao, '')),
        desenhoMontagem: desenhoMontagemResolvido,
        fornecedor: primeiroValor(pegaId(item?.fornecedor), item?.fornecedor, ''),
        fornecedorDescricao: String(primeiroValor(item?.fornecedorDescricao, item?.fornecedorNomeFantasia, item?.fornecedorNome, item?.fornecedor?.nomeFantasia, item?.fornecedor?.name, '')),
    };
};

const resolvePorId = (lista = [], value = null) => {
    const id = pegaId(value);
    if (!id) return value ?? {};
    return lista.find((item) => item.id === id || item._id === id) || value;
};

const normalizaCoordenador = (coordenador = {}) => {
    const id = coordenador?.id || coordenador?._id || '';
    const nome = coordenador?.name || coordenador?.nome || coordenador?.descricao || coordenador?.description || '';

    return {
        ...coordenador,
        id,
        _id: coordenador?._id || id,
        name: coordenador?.name || nome,
        nome: coordenador?.nome || nome,
    };
};

const resolveCoordenador = (lista = [], item = {}) => {
    const coordenadoresNormalizados = Array.isArray(lista)
        ? lista.map((coordenador = {}) => normalizaCoordenador(coordenador))
        : [];

    const resolvidoPorId = resolvePorId(coordenadoresNormalizados, item?.coordenadorDeProjetos);
    if (resolvidoPorId && typeof resolvidoPorId === 'object' && pegaId(resolvidoPorId)) {
        return normalizaCoordenador(resolvidoPorId);
    }

    const nomeBase = String(item?.coordenadorDeProjetosNome || '').trim().toLowerCase();
    if (!nomeBase) {
        const bruto = item?.coordenadorDeProjetos;
        if (typeof bruto === 'object' && bruto) return normalizaCoordenador(bruto);
        return {};
    }

    const porNome = coordenadoresNormalizados.find((coordenador = {}) => {
        const nome = String(coordenador?.name || coordenador?.nome || '').trim().toLowerCase();
        return nome && nome === nomeBase;
    });

    return porNome || {};
};

const proximaSequenciaDisponivel = (listaItens = []) => {
    const sequenciasNumericas = Array.isArray(listaItens)
        ? listaItens
            .map((item = {}) => Number.parseInt(String(item?.sequencia || '').replace(/\D/g, ''), 10))
            .filter((value) => Number.isFinite(value) && value > 0)
            .sort((a, b) => a - b)
        : [];

    const maiorSequencia = sequenciasNumericas.length > 0
        ? sequenciasNumericas[sequenciasNumericas.length - 1]
        : 0;

    return String(maiorSequencia + 1).padStart(3, '0');
};

const disabledEditar = computed(() => {
    if (props.disabled) return true;
    if (conteudoModalRef.value?.isValid === false) return true;
    return false;
});

const GRUPOS_CRIAR_EDITAR_ITENS = [
    'Engenharia Detalhamento - Operação',
    'Engenharia Detalhamento - Supervisor',
    'Ventilador - Operação',
    'Ventilador - Supervisor',
];

const GRUPOS_CAMPOS_SENSIVEIS = [
    'Comercial Sobressalentes - Operação',
    'Comercial Sobressalentes - Supervisor',
    'Administradores',
];

const nomeUsuarioLogado = computed(() => String(primeiroValor(
    usuarioStore?.usuario?.nome,
    usuarioStore?.usuario?.name,
    usuarioStore?.usuario?.login,
    ''
)).trim());

const idUsuarioLogado = computed(() => String(primeiroValor(
    usuarioStore?.usuario?.id,
    usuarioStore?.usuario?._id,
    usuarioStore?.usuario?.userId,
    ''
)).trim());

const grupoUsuarioLogado = computed(() => String(usuarioStore?.usuario?.grupoNome || '').trim());

const usuarioEhCoordenadorDaOS = computed(() => {
    const usuarioId = idUsuarioLogado.value;
    const usuarioNome = normalizaNome(nomeUsuarioLogado.value);
    if (!usuarioId && !usuarioNome) return false;
    const coordenadorId = String(pegaId(dado.value?.coordenadorDeProjetos) || '').trim();
    if (usuarioId && coordenadorId && usuarioId === coordenadorId) return true;
    const coordenadorOS = normalizaNome(extraiNomeCoordenadorOS(dado.value));
    return !!(usuarioNome && coordenadorOS && usuarioNome === coordenadorOS);
});

const usuarioEhCoordenadorContratoJob = computed(() => {
    const usuarioNome = normalizaNome(nomeUsuarioLogado.value);
    const coordenadorContrato = normalizaNome(extraiNomeCoordenadorContratoJob(dado.value));
    return !!(usuarioNome && coordenadorContrato && usuarioNome === coordenadorContrato);
});

const responseOk = (response = {}) => {
    if (response?.status !== 200) return false;
    if (response?.data?.success === false) return false;
    return true;
};

const limparCamposAuditoria = (obj = {}) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;

    const {
        createAt,
        createBy,
        updateAt,
        updateBy,
        deleteAt,
        deleteBy,
        ...rest
    } = obj;

    return rest;
};

const limpaCamposInternos = (value = null) => {
    if (Array.isArray(value)) {
        return value.map((item) => limpaCamposInternos(item));
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    const sanitized = {};

    Object.entries(value).forEach(([key, itemValue]) => {
        if (String(key).startsWith('_')) return;
        sanitized[key] = limpaCamposInternos(itemValue);
    });

    return sanitized;
};

const normalizarRegistroEntity = (registro = {}) => {
    if (!registro || typeof registro !== 'object') {
        const id = pegaId(registro);
        return { id, _id: id };
    }

    const currentData = registro?._currentData || registro?.currentData || {};
    const temDados = currentData && typeof currentData === 'object' && Object.keys(currentData).length > 0;
    const dados = temDados ? currentData : registro;

    return {
        ...dados,
        id: dados?.id || dados?._id || registro?.id || registro?._id || '',
        _id: dados?._id || registro?._id || dados?.id || registro?.id || '',
    };
};

const notificarErro = (mensagem = 'Erro ao executar operação.') => {
    if (vm?.$notfy?.error) {
        vm.$notfy.error(mensagem);
        return;
    }
    window.alert(mensagem);
};

const notificarSucesso = (mensagem = 'Operação concluída com sucesso.') => {
    if (vm?.$notfy?.success) {
        vm.$notfy.success(mensagem);
        return;
    }
    window.alert(mensagem);
};

const abrirDialogConfirmacao = ({ titulo = '', mensagem = '', textoConfirmar = 'Confirmar', onConfirm = null } = {}) => {
    dialogAcaoTitulo.value = titulo;
    dialogAcaoMensagem.value = mensagem;
    dialogAcaoBotaoConfirmar.value = textoConfirmar;
    dialogAcaoCallback.value = onConfirm;
    dialogAcaoVisivel.value = true;
};

const fecharDialogConfirmacao = () => {
    dialogAcaoVisivel.value = false;
    dialogAcaoTitulo.value = '';
    dialogAcaoMensagem.value = '';
    dialogAcaoBotaoConfirmar.value = 'Confirmar';
    dialogAcaoCallback.value = null;
    dialogAcaoExecutando.value = false;
};

const confirmarDialogAcao = async () => {
    if (typeof dialogAcaoCallback.value !== 'function') {
        fecharDialogConfirmacao();
        return;
    }

    dialogAcaoExecutando.value = true;
    try {
        await dialogAcaoCallback.value();
        fecharDialogConfirmacao();
    } catch (error) {
        console.error(error);
        notificarErro('Erro ao executar a ação solicitada.');
        dialogAcaoExecutando.value = false;
    }
};

const recarregarOSAtual = async () => {
    const osId = pegaId(dado.value?.id || dado.value?._id);
    if (!osId) return;
    await preparaModal({ id: osId });
};

const formatarItemParaMensagem = (item = {}) => {
    return String(
        primeiroValor(
            item?.sequencia,
            item?.itemCodigoExt,
            item?.itemCodigo,
            item?.descricao,
            pegaId(item?.id || item?._id),
            'item-sem-identificacao'
        )
    ).trim();
};

const formatarMoedaBRL = (valor = 0) => {
    const numero = UtilNumber.toNumber(valor);
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(numero);
};

const validarValorDisponivelNoSalvarItem = (itemCandidato = {}, idItemOriginal = '') => {
    const valorDisponivel = UtilNumber.toNumber(dado.value?.valorDisponivel);
    const idOriginalNormalizado = String(pegaId(idItemOriginal) || '').trim();
    const listaAtual = Array.isArray(itensDaOS.value) ? itensDaOS.value : [];

    const obterValorTotalParaLimite = (itemAtual = {}) => {
        const valorTotalInformado = primeiroValor(itemAtual?.valorTotal, itemAtual?.precoTotal, null);
        const valorTotalNumero = UtilNumber.toNumber(valorTotalInformado);
        const quantidade = UtilNumber.toNumber(primeiroValor(itemAtual?.quantidade, itemAtual?.qtd, itemAtual?.quantidadeSolicitada, 0));
        const valorUnitario = UtilNumber.toNumber(primeiroValor(itemAtual?.valorUnitario, itemAtual?.valorUnit, itemAtual?.precoUnitario, 0));
        const valorTotalCalculado = UtilNumber.round(quantidade * valorUnitario);

        if (valorTotalInformado === null || valorTotalInformado === undefined || valorTotalInformado === '') {
            return valorTotalCalculado;
        }

        if (valorTotalNumero <= 0 && valorTotalCalculado > 0) {
            return valorTotalCalculado;
        }

        return valorTotalNumero;
    };

    let substituiuItem = false;
    const listaComCandidato = listaAtual.map((itemAtual = {}, index) => {
        const itemNormalizado = normalizaItemDaOS(itemAtual, index);
        const idAtual = String(pegaId(itemNormalizado?.id || itemNormalizado?._id) || '').trim();

        if (idOriginalNormalizado && idAtual && idAtual === idOriginalNormalizado) {
            substituiuItem = true;
            return itemCandidato;
        }

        return itemNormalizado;
    });

    if (!substituiuItem) {
        listaComCandidato.push(itemCandidato);
    }

    const somaValorTotal = UtilNumber.round(
        listaComCandidato.reduce((acumulador, itemAtual = {}) => (
            acumulador + obterValorTotalParaLimite(itemAtual)
        ), 0)
    );

    const limite = UtilNumber.round(valorDisponivel);
    const excedeuLimite = (somaValorTotal - limite) > 0.0001;

    return {
        permitido: !excedeuLimite,
        somaValorTotal,
        valorDisponivel: limite,
    };
};

const atualizaItemOSComStatus = async ({ itemId = '', ordemId = '', novoStatus = '' } = {}) => {
    const idItem = pegaId(itemId);
    const idOrdem = pegaId(ordemId);

    if (!idItem || !idOrdem || !novoStatus) return;

    // Atualiza via PUT com o registro completo + alteração de status,
    // preservando todos os campos exigidos pelo backend.
    await atualizaRegistroCompletoComAlteracoes(Schema.ORDEM_DE_SERVICO_ITENS, idItem, {
        status: novoStatus,
    });
};

const atualizaTodosItensDaOSComStatus = async (novoStatus = '') => {
    const ordemId = pegaId(dado.value?.id || dado.value?._id);
    if (!ordemId || !novoStatus) return;

    const itens = Array.isArray(itensDaOS.value) ? itensDaOS.value : [];
    for (const item of itens) {
        const idItem = pegaId(item?.id || item?._id);
        if (!idItem) continue;
        await atualizaItemOSComStatus({ itemId: idItem, ordemId, novoStatus });
    }
};

const resolveItemDaOSMaeParaAtualizar = async () => {
    const osMaeId = pegaId(dado.value?.osMae);
    const referenciaItem = String(pegaId(dado.value?.itemOrdemDeServico) || '').trim();

    if (!osMaeId) {
        throw new Error('OS mãe não informada para localizar item de origem.');
    }

    let itensMae = [];

    // Prioriza os childrens da OS mãe para evitar dependência de endpoints que em alguns ambientes retornam 400.
    try {
        const osMaeCompleta = await OrdemDeServicoService.pegaOrdemDeServicoCompleta(osMaeId);
        const childItens = Array.isArray(osMaeCompleta?.data?._childrens?.OrdemDeServicoItens)
            ? osMaeCompleta.data._childrens.OrdemDeServicoItens
            : (Array.isArray(osMaeCompleta?.data?._childrens?.ordemDeServicoItens)
                ? osMaeCompleta.data._childrens.ordemDeServicoItens
                : []);

        if (childItens.length > 0) {
            itensMae = childItens.map((item = {}, index) => normalizaItemDaOS(item, index));
        }
    } catch (_) {
        // fallback abaixo
    }

    if (!itensMae.length) {
        const responseItensMae = await OrdemDeServicoService.pegaItensDaOrdemDeServico(osMaeId, []);
        if (Array.isArray(responseItensMae?.data) && responseItensMae.data.length > 0) {
            itensMae = responseItensMae.data.map((item = {}, index) => normalizaItemDaOS(item, index));
        }
    }

    if (!itensMae.length) {
        throw new Error('OS mãe sem itens para atualização.');
    }

    const matchPorIdItemOS = referenciaItem
        ? itensMae.find((item = {}) => String(pegaId(item?.id || item?._id) || '').trim() === referenciaItem)
        : null;
    if (matchPorIdItemOS) return matchPorIdItemOS;

    // Em muitas OS de Fabricação, itemOrdemDeServico guarda o ID do cadastro de Item (não o ID do OrdemDeServicoItens).
    const matchPorItemMaster = referenciaItem
        ? itensMae.find((item = {}) => String(pegaId(item?.item) || '').trim() === referenciaItem)
        : null;
    if (matchPorItemMaster) return matchPorItemMaster;

    const descricaoFilha = normalizaTextoComparavel(dado.value?.descricao || '');
    const matchPorDescricao = descricaoFilha
        ? itensMae.find((item = {}) => normalizaTextoComparavel(item?.descricao || '') === descricaoFilha)
        : null;
    if (matchPorDescricao) return matchPorDescricao;

    throw new Error('Não foi possível identificar o item da OS mãe vinculado à fabricação.');
};

const atualizaItemOSMaeComStatus = async (novoStatus = '') => {
    const osMaeId = pegaId(dado.value?.osMae);
    const itemMae = await resolveItemDaOSMaeParaAtualizar();
    const itemMaeId = pegaId(itemMae?.id || itemMae?._id);

    if (!osMaeId || !itemMaeId || !novoStatus) {
        throw new Error('Não foi possível localizar OS mãe/item de origem para atualizar o status.');
    }

    await atualizaItemOSComStatus({ itemId: itemMaeId, ordemId: osMaeId, novoStatus });
};

const atualizaRegistroCompletoComAlteracoes = async (schema = '', id = '', alteracoes = {}) => {
    if (!schema || !id) {
        throw new Error('Schema e id são obrigatórios para atualização.');
    }

    const carregarRegistroCompleto = async () => {
        try {
            const responseDireto = await ERP.getOneById(schema, id);
            if (responseDireto?.status === 200) {
                const registro = normalizarRegistroEntity(responseDireto?.data || {});
                if (pegaId(registro?.id || registro?._id)) return registro;
            }
        } catch (_) {
            // tenta próximos fallbacks
        }

        try {
            const responseLista = await ERP.getManyById(schema, [id]);
            const lista = Array.isArray(responseLista?.data)
                ? responseLista.data
                : (Array.isArray(responseLista?.data?.data) ? responseLista.data.data : []);

            const registroBruto = lista.find((item = {}) => String(pegaId(item?.id || item?._id) || '').trim() === String(id).trim()) || lista[0] || null;
            if (registroBruto) {
                const registro = normalizarRegistroEntity(registroBruto);
                if (pegaId(registro?.id || registro?._id)) return registro;
            }
        } catch (_) {
            // tenta próximo fallback
        }

        try {
            const service = new EntityService(schema);
            const responseEntity = await service.getById(id);
            const registro = normalizarRegistroEntity(responseEntity?.data?.data || responseEntity?.data || {});
            if (pegaId(registro?.id || registro?._id)) return registro;
        } catch (_) {
            // sem fallback adicional
        }

        return null;
    };

    const registroAtual = await carregarRegistroCompleto();
    if (!registroAtual) {
        // Fallback final: tenta update parcial para não bloquear fluxo de cancelamento.
        const payloadParcial = limpaCamposInternos({ id, ...alteracoes });
        const responseParcial = await ERP.putOne(schema, payloadParcial);
        if (responseParcial?.status === 200) {
            return responseParcial;
        }
        throw new Error(`Falha ao carregar registro ${schema}/${id}.`);
    }

    const payload = limpaCamposInternos(limparCamposAuditoria({
        ...registroAtual,
        ...alteracoes,
        id,
    }));

    const responseUpdate = await ERP.putOne(schema, payload);
    if (responseUpdate?.status !== 200) {
        throw new Error(`Falha ao atualizar registro ${schema}/${id}.`);
    }

    return responseUpdate;
};

const softDeletePorCampo = async ({ schema = '', campo = '', valor = '' } = {}) => {
    if (!schema || !campo || !valor) return;

    let responseBusca = null;
    try {
        responseBusca = await ERP.getManyByValue(schema, campo, valor);
    } catch (_) {
        return;
    }

    const listaBruta = Array.isArray(responseBusca?.data)
        ? responseBusca.data
        : (Array.isArray(responseBusca?.data?.data) ? responseBusca.data.data : []);

    const lista = Array.isArray(listaBruta) ? listaBruta.map((item) => normalizarRegistroEntity(item)) : [];
    if (!lista.length) return;

    const payload = lista.map((registro = {}) => limpaCamposInternos(limparCamposAuditoria({
        ...registro,
        id: pegaId(registro?.id || registro?._id),
        isDeleted: true,
    }))).filter((item) => !!item?.id);

    if (!payload.length) return;

    const response = await ERP.putMany(schema, payload);
    if (response?.status !== 200) {
        // Vínculos auxiliares não devem impedir o cancelamento da OS.
        console.warn(`[OS][cancelar] Falha ao remover vínculo em ${schema}`, response);
    }
};

const removeOSFabricacaoComRelacionamentos = async () => {
    const osId = pegaId(dado.value?.id || dado.value?._id);
    const osCodigo = String(dado.value?.codigo || '').trim();
    const itemMaeId = String(pegaId(dado.value?.itemOrdemDeServico) || '').trim();

    if (!osId) {
        throw new Error('OS de fabricação não identificada para remoção.');
    }

    const idsItensDaFabricacao = [...new Set([
        ...(Array.isArray(dado.value?.itens) ? dado.value.itens.map((id) => pegaId(id)) : []),
        ...(Array.isArray(itensDaOS.value) ? itensDaOS.value.map((item = {}) => pegaId(item?.id || item?._id)) : []),
    ].map((id) => String(id || '').trim()).filter(Boolean))];

    for (const itemId of idsItensDaFabricacao) {
        await atualizaRegistroCompletoComAlteracoes(Schema.ORDEM_DE_SERVICO_ITENS, itemId, { isDeleted: true });
    }

    await atualizaRegistroCompletoComAlteracoes(Schema.ORDEM_DE_SERVICO, osId, { isDeleted: true });

    if (osCodigo) {
        await softDeletePorCampo({ schema: 'OrdemDeFabricacao', campo: 'osFilha', valor: osCodigo });
    }

    if (itemMaeId) {
        await softDeletePorCampo({ schema: 'OrdemDeFabricacao', campo: 'osItem', valor: itemMaeId });
        await softDeletePorCampo({ schema: 'ItensOrdemDeFabricacao', campo: 'itemOrdemDeServico', valor: itemMaeId });
    }
};

const obterStatusItem = (item = {}) => String(primeiroValor(item?.status, '') || '').trim().toUpperCase();

const itensComStatusNaoPermitidosCancelar = computed(() => {
    const itens = Array.isArray(itensDaOS.value) ? itensDaOS.value : [];
    return itens.filter((item = {}) => {
        const status = obterStatusItem(item);
        return status !== 'LA' && status !== 'CN';
    });
});

const itensComStatusNaoPermitidosFinalizar = computed(() => {
    const itens = Array.isArray(itensDaOS.value) ? itensDaOS.value : [];
    return itens.filter((item = {}) => {
        const status = obterStatusItem(item);
        return status !== 'ET' && status !== 'CN';
    });
});

const osComItensETeCN = computed(() => {
    const itens = Array.isArray(itensDaOS.value) ? itensDaOS.value : [];
    const statusSet = new Set(itens.map((item = {}) => obterStatusItem(item)));
    return statusSet.has('ET') && statusSet.has('CN');
});

const itensAptosParaReabrir = computed(() => {
    const itens = Array.isArray(itensDaOS.value) ? itensDaOS.value : [];
    if (!itens.length) return false;

    return itens.every((item = {}) => {
        const status = obterStatusItem(item);
        return status === 'ET' || status === 'CN';
    });
});

const osFinalizada = computed(() => {
    const itens = Array.isArray(itensDaOS.value) ? itensDaOS.value : [];
    if (!itens.length) return false;
    return itens.every((item = {}) => {
        const status = obterStatusItem(item);
        return status === 'FEITO';
    });
});

const podeExibirBotaoFinalizar = computed(() => {
    if (!osTipoFabricacao.value) return false;
    return !osFinalizada.value;
});

const botaoFinalizarDesabilitado = computed(() => {
    if (props.disabled || dialogAcaoExecutando.value) return true;
    return itensComStatusNaoPermitidosFinalizar.value.length > 0;
});

const podeExibirBotaoCancelar = computed(() => {
    if (!osTipoFabricacao.value) return false;
    return true;
});

const podeExibirBotaoReabrir = computed(() => {
    if (!osTipoFabricacao.value) return false;
    return itensAptosParaReabrir.value;
});

const acaoFinalizarOS = async () => {
    try {
        await atualizaItemOSMaeComStatus('RE');
        await recarregarOSAtual();
        notificarSucesso('OS de Fabricação finalizada com sucesso.');
    } catch (error) {
        console.error(error);
        notificarErro('Erro ao finalizar OS de Fabricação.');
    }
};

const acaoCancelarOS = async () => {
    try {
        await removeOSFabricacaoComRelacionamentos();
        await atualizaItemOSMaeComStatus('LA');
        notificarSucesso('OS de Fabricação cancelada com sucesso.');
        await fecharModal();
    } catch (error) {
        console.error(error);
        notificarErro('Erro ao cancelar OS de Fabricação.');
    }
};

const acaoReabrirOS = async () => {
    try {
        await atualizaItemOSMaeComStatus('SF');
        await atualizaTodosItensDaOSComStatus('LA');
        await recarregarOSAtual();
        notificarSucesso('OS de Fabricação reaberta com sucesso.');
    } catch (error) {
        console.error(error);
        notificarErro('Erro ao reabrir OS de Fabricação.');
    }
};

const solicitarFinalizacaoOS = async () => {
    const itensInvalidos = itensComStatusNaoPermitidosFinalizar.value;
    if (itensInvalidos.length > 0) {
        const itensTexto = itensInvalidos.map((item) => formatarItemParaMensagem(item)).join(', ');
        window.alert(`A OS não pode ser finalizada pois o item ou os itens ${itensTexto} não está(ao) com o status ET ou CN.`);
        return;
    }

    abrirDialogConfirmacao({
        titulo: 'Finalizar OS',
        mensagem: 'Deseja Finalizar a OS de Fabricação ?',
        textoConfirmar: 'Concluir',
        onConfirm: acaoFinalizarOS,
    });
};

const solicitarCancelamentoOS = async () => {
    if (osComItensETeCN.value) {
        window.alert('OS Já Encerrada.');
        return;
    }

    const itensInvalidos = itensComStatusNaoPermitidosCancelar.value;
    if (itensInvalidos.length > 0) {
        const itensTexto = itensInvalidos.map((item) => formatarItemParaMensagem(item)).join(', ');
        window.alert(`A OS não pode ser cancelada pois o item ou os itens ${itensTexto} não está(ao) com o status LA ou CN.`);
        return;
    }

    abrirDialogConfirmacao({
        titulo: 'Cancelar OS',
        mensagem: 'Ao Confirmar a OS de fabricação será cancelada e o Item na OS principal irá retornar ao status LA.',
        textoConfirmar: 'Confirmar',
        onConfirm: acaoCancelarOS,
    });
};

const solicitarReaberturaOS = async () => {
    try {
        const itemMae = await resolveItemDaOSMaeParaAtualizar();
        const itemMaeId = pegaId(itemMae?.id || itemMae?._id);
        if (!itemMaeId) {
            window.alert('Não foi possível validar o item da OS principal para reabertura.');
            return;
        }

        const statusItemMae = String(primeiroValor(itemMae?.status, '') || '').trim().toUpperCase();

        if (statusItemMae !== 'RE') {
            window.alert('Não é possível reabrir esta OS: o item da OS principal não está com status RE.');
            return;
        }

        abrirDialogConfirmacao({
            titulo: 'Reabrir OS',
            mensagem: 'Você irá reabrir a OS e terá de realizar novamente as etapas de fabricação.',
            textoConfirmar: 'Confirmar',
            onConfirm: acaoReabrirOS,
        });
    } catch (error) {
        console.error(error);
        notificarErro('Erro ao validar reabertura da OS de Fabricação.');
    }
};

const validaItensPersistidosNaOS = async (ordemId = '', idsEsperados = []) => {
    if (!ordemId) return true;

    const respostaOS = await OrdemDeServicoService.pegaOrdemDeServicoCompleta(ordemId);
    if (!responseOk(respostaOS)) {
        // Em alguns registros legados o GET por id da OS pode falhar no backend,
        // então não bloqueamos o fluxo de gravação por falha de leitura.
        return true;
    }

    const itensPersistidos = Array.isArray(respostaOS?.data?.itens)
        ? respostaOS.data.itens.map((item = {}) => pegaId(item)).filter(Boolean)
        : [];

    const esperados = Array.isArray(idsEsperados)
        ? idsEsperados.map((id) => String(id || '').trim()).filter(Boolean)
        : [];

    return esperados.every((id) => itensPersistidos.includes(id));
};

const podeEditarDatasEtapas = computed(() => {
    if (props.disabled) return false;
    const grupo = grupoUsuarioLogado.value;
    if (GRUPOS_CAMPOS_SENSIVEIS.includes(grupo)) return true;
    return usuarioEhCoordenadorDaOS.value;
});

const usuarioEhGrupoEngenharia = computed(() => GRUPOS_CRIAR_EDITAR_ITENS.includes(grupoUsuarioLogado.value));
const usuarioEhGrupoSobressalentes = computed(() => GRUPOS_CAMPOS_SENSIVEIS.includes(grupoUsuarioLogado.value));

// Coordenador de contrato só pode atuar quando item estiver em LC.
const coordenadorContratoSomenteLC = computed(() => (
    !props.disabled
    && usuarioEhCoordenadorContratoJob.value
    && !usuarioEhGrupoSobressalentes.value
    && !usuarioEhGrupoEngenharia.value
));

const podeAdicionarItensOS = computed(() => {
    if (props.disabled) return false;
    if (usuarioEhGrupoSobressalentes.value) return true;
    return usuarioEhGrupoEngenharia.value;
});

const podeEditarItensOS = computed(() => {
    if (props.disabled) return false;
    if (usuarioEhGrupoSobressalentes.value) return true;
    if (usuarioEhGrupoEngenharia.value) return true;
    return coordenadorContratoSomenteLC.value;
});

const statusPermitidosEdicaoItem = computed(() => {
    if (usuarioEhGrupoSobressalentes.value) return ['ED', 'LC'];
    return [];
});

// Para coordenador de contrato: apenas valor unitário + data necessidade e só no status LC.
const podeEditarValorUnitarioItem = computed(() => {
    if (props.disabled) return false;
    if (usuarioEhGrupoSobressalentes.value) return true;
    return coordenadorContratoSomenteLC.value;
});

const podeEditarDataNecessidadeItem = computed(() => {
    if (props.disabled) return false;
    if (usuarioEhGrupoSobressalentes.value) return true;
    return coordenadorContratoSomenteLC.value;
});

// Engenharia pode lançar item, mas não pode editar data necessidade.
const dataNecessidadeSomenteLeituraItem = computed(() => {
    if (props.disabled) return true;
    if (usuarioEhGrupoSobressalentes.value) return false;
    return usuarioEhGrupoEngenharia.value;
});

const osTipoFabricacao = computed(() => isTipoFabricacao(dado.value?.osTipo));

const canAddItemOSComputed = computed(() => {
    if (osTipoFabricacao.value) return false;
    return podeAdicionarItensOS.value;
});

const canEditItemOSComputed = computed(() => {
    if (osTipoFabricacao.value) return false;
    return podeEditarItensOS.value;
});

const alteraDado = (update = {}) => {
    dado.value = UtilObject.merge(dado.value, update);
};

const montaPayloadOrdemDeServico = () => {
    return {
        id:                         dado.value?.id                         || '',
        descricao:                  dado.value?.descricao                  || '',
        osTipo:                     dado.value?.osTipo                     || 'Padrão',
        job:                        pegaId(dado.value?.job)               || '',
        jobItem:                    pegaId(dado.value?.jobItem)           || '',
        itemOrdemDeServico:         dado.value?.itemOrdemDeServico?.id     || dado.value?.itemOrdemDeServico || '',
        coordenadorDeProjetos:      dado.value?.coordenadorDeProjetos?.id  || dado.value?.coordenadorDeProjetos || '',
        osMae:                      dado.value?.osMae?.id                  || dado.value?.osMae || '',
        osQuantidade:               dado.value?.osQuantidade               ?? 0,
        valorDisponivel:            dado.value?.valorDisponivel            ?? 0,
        dataSolicitacao:            dado.value?.dataSolicitacao            || '',
        dataEncerramentoPrevista:   dado.value?.dataEncerramentoPrevista   || '',
        dataEncerramento:           dado.value?.dataEncerramento           || '',
        dataDetalhamentoPrevista:   dado.value?.dataDetalhamentoPrevista   || '',
        dataDetalhamento:           dado.value?.dataDetalhamento           || '',
        possuiDetalhamento:         dado.value?.possuiDetalhamento         ?? false,
        dataMontagemPrevista:       dado.value?.dataMontagemPrevista       || '',
        dataMontagem:               dado.value?.dataMontagem               || '',
        possuiMontagem:             dado.value?.possuiMontagem             ?? false,
        dataFabricacaoPrevista:     dado.value?.dataFabricacaoPrevista     || '',
        dataFabricacao:             dado.value?.dataFabricacao             || '',
        itens:                      Array.isArray(dado.value?.itens) ? dado.value.itens : [],
    };
};

const editarOrdemDeServico = async () => {
    const ordemId = pegaId(dado.value?.id || dado.value?._id);

    const sincronizacaoItens = await OrdemDeServicoService.sincronizaItensDaOS(ordemId, itensDaOS.value || []);
    if (sincronizacaoItens?.status !== 200) {
        throw new Error('Falha ao salvar itens da Ordem de Serviço');
    }

    itensDaOS.value = Array.isArray(sincronizacaoItens?.data)
        ? sincronizacaoItens.data.map((item = {}, index) => normalizaItemDaOS(item, index))
        : itensDaOS.value;

    dado.value.itens = Array.isArray(sincronizacaoItens?.ids) ? sincronizacaoItens.ids : [];

    const payload = montaPayloadOrdemDeServico();

    const response = await OrdemDeServicoService.editaOrdemDeServico(payload);
    if (!responseOk(response)) {
        throw new Error('Falha ao editar Ordem de Serviço');
    }

    const vinculoOk = await validaItensPersistidosNaOS(ordemId, dado.value.itens || []);
    if (!vinculoOk) {
        throw new Error('Falha ao persistir vínculo de itens na Ordem de Serviço');
    }

    await fecharModal();
};

const abrirModal = async (dadoParaEditar = {}) => {
    await preparaModal(dadoParaEditar);
    await modalFullscreenRef.value?.abrirModal();
};

const fecharModal = async () => {
    await modalFullscreenRef.value?.fecharModal();
};

const aoFecharModal = async () => {
    await props.onClose?.();
};

const preparaModal = async (item = {}) => {
    const ordemDeServicoCompletaResponse = await OrdemDeServicoService.pegaOrdemDeServicoCompleta(pegaId(item?.id || item?._id));
    const ordemDeServicoCompleta = ordemDeServicoCompletaResponse?.data || {};

    const itemCorrigido = {
        ...item,
        ...ordemDeServicoCompleta,
        itens: Array.isArray(ordemDeServicoCompleta?.itens) ? ordemDeServicoCompleta.itens : (Array.isArray(item?.itens) ? item.itens : []),
    };

    const jobResolvido = resolvePorId(props.jobs, itemCorrigido.job);
        // Preserva campos do job original que podem não estar no array resumido de jobs (ex: coordenadorDeContratos)
        const jobOriginal = itemCorrigido.job && typeof itemCorrigido.job === 'object' ? itemCorrigido.job : {};
        const jobResolvidoCompleto = jobResolvido && typeof jobResolvido === 'object'
            ? { ...jobOriginal, ...jobResolvido }
            : jobResolvido;
    const coordenadorResolvido = resolveCoordenador(props.coordenadores, itemCorrigido);
    const osMaeResolvida = resolvePorId(props.ordensDeServico, itemCorrigido.osMae);
    const itemDaOSId = pegaId(itemCorrigido.itemOrdemDeServico);

    let listaItensDaOS = [];
    let itemDaOS = {};
    let osFilhasResponse = { status: 'fulfilled', value: { data: [] } };
    let itemDaOSResolvido = {};

    const [itensDaOSResponse, itemDaOSResponse, _osFilhasResponse] = await Promise.allSettled([
        OrdemDeServicoService.pegaItensDaOrdemDeServico(itemCorrigido.id || itemCorrigido._id, itemCorrigido.itens || []),
        OrdemDeServicoService.pegaItemDaOS(itemDaOSId),
        OrdemDeServicoService.pegaOsFilhas(itemCorrigido.id || itemCorrigido._id),
    ]);

    osFilhasResponse = _osFilhasResponse;

    listaItensDaOS = itensDaOSResponse.status === 'fulfilled' && Array.isArray(itensDaOSResponse.value?.data)
        ? itensDaOSResponse.value.data
        : [];
    itemDaOS = itemDaOSResponse.status === 'fulfilled' ? (itemDaOSResponse.value?.data || {}) : {};

    itemDaOSResolvido = resolvePorId(listaItensDaOS, itemCorrigido.itemOrdemDeServico)
        || resolvePorId([itemDaOS], itemCorrigido.itemOrdemDeServico)
        || itemCorrigido.itemOrdemDeServico
        || {};

    // A tabela deve listar somente os itens da OS atual (campo "itens" da OS).
    // Não injeta itemOrdemDeServico da OS mãe para evitar linha fantasma/duplicada.
    itensDaOS.value = [...listaItensDaOS].map((itemLista = {}, index) => {
        const itemNormalizado = normalizaItemDaOS(itemLista, index);
        return {
            ...itemNormalizado,
            display: itemNormalizado?.sequencia || itemNormalizado?.descricao || itemNormalizado?.itemCodigoExt || itemNormalizado?.id || '',
        };
    });

    const idsItensCarregados = Array.isArray(itensDaOS.value)
        ? itensDaOS.value.map((item = {}) => pegaId(item?.id || item?._id)).filter(Boolean)
        : [];

    // Buscar histórico com tratamento de erro
    try {
        const historicoResponse = await OrdemDeServicoService.pegaHistoricoItemDaOS(
            itensDaOS.value,
            pegaId(itemCorrigido.id || itemCorrigido._id)
        );
        historico.value = Array.isArray(historicoResponse?.data) ? historicoResponse.data : [];
    } catch (erroHistorico) {
        console.error('Erro ao buscar histórico, continuando sem ele:', erroHistorico);
        historico.value = [];
    }

    osFilhas.value = osFilhasResponse.status === 'fulfilled' && Array.isArray(osFilhasResponse.value?.data)
        ? osFilhasResponse.value.data
        : [];

    dado.value = {
        id:                         itemCorrigido.id                         ?? '',
        codigo:                     itemCorrigido.codigo                     ?? '',
        descricao:                  itemCorrigido.descricao                  ?? '',
        osTipo:                     itemCorrigido.osTipo                     ?? 'Padrão',
        job:                        jobResolvidoCompleto            ?? itemCorrigido.job ?? {},
        jobCodigo:                  itemCorrigido.jobCodigo                  ?? '',
        jobItem:                    itemCorrigido.jobItem                    ?? {},
        jobItemSequencia:           itemCorrigido.jobItemSequencia           ?? '',
        itemOrdemDeServico:         itemDaOSResolvido               ?? itemCorrigido.itemOrdemDeServico ?? {},
        itemOrdemDeServicoDetalhe:  itemDaOS                        ?? {},
        coordenadorDeProjetos:      coordenadorResolvido            ?? itemCorrigido.coordenadorDeProjetos ?? {},
        coordenadorDeProjetosNome:  itemCorrigido.coordenadorDeProjetosNome ?? coordenadorResolvido?.name ?? coordenadorResolvido?.nome ?? '',
        osMae:                      osMaeResolvida                  ?? itemCorrigido.osMae ?? {},
        osMaeCodigo:                itemCorrigido.osMaeCodigo ?? osMaeResolvida?.codigo ?? '',
        osQuantidade:               itemCorrigido.osQuantidade               ?? 0,
        valorDisponivel:            itemCorrigido.valorDisponivel            ?? 0,
        dataSolicitacao:            itemCorrigido.dataSolicitacao            ?? '',
        dataEncerramentoPrevista:   itemCorrigido.dataEncerramentoPrevista   ?? '',
        dataEncerramento:           itemCorrigido.dataEncerramento           ?? '',
        dataDetalhamentoPrevista:   itemCorrigido.dataDetalhamentoPrevista   ?? '',
        dataDetalhamento:           itemCorrigido.dataDetalhamento           ?? '',
        possuiDetalhamento:         itemCorrigido.possuiDetalhamento         ?? false,
        dataMontagemPrevista:       itemCorrigido.dataMontagemPrevista       ?? '',
        dataMontagem:               itemCorrigido.dataMontagem               ?? '',
        possuiMontagem:             itemCorrigido.possuiMontagem             ?? false,
        dataFabricacaoPrevista:     itemCorrigido.dataFabricacaoPrevista     ?? '',
        dataFabricacao:             itemCorrigido.dataFabricacao             ?? '',
        itens:                      Array.isArray(itemCorrigido.itens) ? itemCorrigido.itens : [],
    };
};

const adicionarItem = () => {
    itemSelecionado.value = {
        sequencia: proximaSequenciaDisponivel(itensDaOS.value),
        status: 'ED',
        quantidade: 0,
    };
    modalItemOS.value = true;
};

const editarItem = (item) => {
    itemSelecionado.value = { ...item };
    modalItemOS.value = true;
};

const removerItem = async (item) => {
    const ordemId = pegaId(dado.value?.id || dado.value?._id);
    const idItem = pegaId(item);
    itensDaOS.value = itensDaOS.value.filter((i) => pegaId(i) !== idItem);
    dado.value.itens = itensDaOS.value
        .map((i) => pegaId(i))
        .filter((id) => !!id && !String(id).startsWith('tmp-'));

    if (ordemId) {
        const response = await OrdemDeServicoService.editaOrdemDeServico(montaPayloadOrdemDeServico());
        if (!responseOk(response)) {
            console.error('[OS][removerItem] Falha ao persistir vínculo de itens na OS', response);
            throw new Error('Falha ao persistir itens na Ordem de Serviço');
        }
    }
};

const salvarItem = async (item) => {
    const ordemId = pegaId(dado.value?.id || dado.value?._id);
    const itemNormalizadoBase = normalizaItemDaOS(item);
    const idItemOriginal = pegaId(itemNormalizadoBase?.id || itemNormalizadoBase?._id);
    const validacaoValorDisponivel = validarValorDisponivelNoSalvarItem(itemNormalizadoBase, idItemOriginal);

    if (!validacaoValorDisponivel.permitido) {
        notificarErro(
            `Valor total dos itens (${formatarMoedaBRL(validacaoValorDisponivel.somaValorTotal)}) excede o valor disponível da OS (${formatarMoedaBRL(validacaoValorDisponivel.valorDisponivel)}). Ajuste o item para continuar.`
        );
        return;
    }

    const payloadSalvarItem = {
        ...itemNormalizadoBase,
        itens: itensDaOS.value.map((itemAtual = {}, index) => normalizaItemDaOS(itemAtual, index)),
        atual: Array.isArray(itensDaOS.value) ? itensDaOS.value.length : 0,
        message: 'SEQ OS-ITEM',
    };
    let itemNormalizado = itemNormalizadoBase;

    console.log('[OS][salvarItem] INICIANDO', { ordemId, item, payloadSalvarItem });

    let respostaSalvar = null;
    if (ordemId) {
        respostaSalvar = await OrdemDeServicoService.salvaItemDaOS(payloadSalvarItem, ordemId);
        console.log('[OS][salvarItem] RESPOSTA salvarItemDaOS', respostaSalvar);
        if (respostaSalvar?.status !== 200) {
            console.error('[OS][salvarItem] Falha ao salvar item', respostaSalvar);
            throw new Error(`Falha ao salvar item da Ordem de Serviço (${respostaSalvar?.status || 'sem-status'})`);
        }

        const itemRetornado = normalizaItemDaOS(respostaSalvar?.data || {});
        const idPersistido = ehIdPersistidoLocal(pegaId(itemRetornado)) ? pegaId(itemRetornado) : '';
        if (!idPersistido) {
            console.error('[OS][salvarItem] Item salvo sem id persistido válido', { respostaSalvar, itemRetornado, itemNormalizadoBase });
            throw new Error('Item não retornou id persistido para vínculo na OS');
        }
        itemNormalizado = normalizaItemDaOS({
            ...itemRetornado,
            ...itemNormalizadoBase,
            id: idPersistido,
            _id: idPersistido,
        });
    }

    const idItem = pegaId(itemNormalizado);
    console.log('[OS][salvarItem] idItem resolvido', { idItem, itemNormalizado });

    if (ordemId && (!idItem || String(idItem).startsWith('tmp-') || String(idItem).startsWith('os-item-'))) {
        console.error('[OS][salvarItem] Item sem id persistido após salvar', { item, itemNormalizado, respostaSalvar });
        throw new Error('Item não retornou id persistido para vínculo na OS');
    }

    if (idItem && !String(idItem).startsWith('tmp-')) {
        const idx = itensDaOS.value.findIndex((i) => {
            const idAtual = pegaId(i);
            return idAtual === idItem || (idItemOriginal && idAtual === idItemOriginal);
        });

        if (idx !== -1) {
            itensDaOS.value[idx] = itemNormalizado;
        } else {
            itensDaOS.value.push(itemNormalizado);
        }
    } else {
        itemNormalizado.id = 'tmp-' + Math.random().toString(36).slice(2, 11);
        itemNormalizado._id = itemNormalizado.id;
        itensDaOS.value.push(itemNormalizado);
    }

    itensDaOS.value = [...itensDaOS.value];


    // Só IDs válidos e únicos
    dado.value.itens = Array.from(new Set(
        itensDaOS.value
            .map((i) => pegaId(i))
            .filter((id) => !!id && !String(id).startsWith('tmp-'))
            .filter((id) => ehIdPersistidoLocal(id))
    ));
    console.log('[OS][salvarItem] itens vinculados na OS (após filtro)', dado.value.itens);

    // Após salvar item, garantir update do array de itens na OS
    if (ordemId) {
        const payloadUpdate = montaPayloadOrdemDeServico();
        console.log('[OS][salvarItem] payload enviado para editaOrdemDeServico', payloadUpdate);
        const response = await OrdemDeServicoService.editaOrdemDeServico(payloadUpdate);
        console.log('[OS][salvarItem] RESPOSTA editaOrdemDeServico', response);
        if (!responseOk(response)) {
            console.error('[OS][salvarItem] Falha ao persistir vínculo de itens na OS', response);
            throw new Error('Falha ao persistir vínculo do item na Ordem de Serviço');
        }

        // Logar resposta da OS após update
        const respostaOS = await OrdemDeServicoService.pegaOrdemDeServicoCompleta(ordemId);
        console.log('[OS][salvarItem] resposta pegaOrdemDeServicoCompleta após update', respostaOS);
        const vinculoOk = await validaItensPersistidosNaOS(ordemId, dado.value.itens || []);
        console.log('[OS][salvarItem] vinculoOk', vinculoOk);
        if (!vinculoOk) {
            console.error('[OS][salvarItem] Falha na validação pós-update. Itens na OS:', dado.value.itens, 'Resposta OS:', respostaOS);
            throw new Error('Item salvo, mas não persistiu no vínculo da Ordem de Serviço');
        }

        // Recarrega os itens a partir do backend para garantir que o modal reflita o estado persistido
        const respostaItensAtualizados = await OrdemDeServicoService.pegaItensDaOrdemDeServico(ordemId, []);
        if (responseOk(respostaItensAtualizados) && Array.isArray(respostaItensAtualizados?.data)) {
            itensDaOS.value = respostaItensAtualizados.data.map((item = {}, index) => normalizaItemDaOS(item, index));
            dado.value.itens = itensDaOS.value
                .map((i) => pegaId(i))
                .filter((id) => !!id && !String(id).startsWith('tmp-'))
                .filter((id) => ehIdPersistidoLocal(id));
        }
    }

    // Fechar modal de item após salvar
    modalItemOS.value = false;
    itemSelecionado.value = null;
    console.log('[OS][salvarItem] FIM fluxo salvarItem');
};

const fecharModalItem = () => {
    modalItemOS.value = false;
    itemSelecionado.value = null;
};

defineExpose({ abrirModal, fecharModal });
</script>

<template>
    <ModalFullscreen
        ref="modalFullscreenRef"
        :dado="dado"
        :disabled="props.disabled"
        @onClose="aoFecharModal"
    >
        <template #modal-navegacao-titulo>
            Editar Ordem de Serviço
        </template>

        <template #modal-navegacao-botoes>
            <BotaoTextoBase
                v-if="podeExibirBotaoFinalizar"
                color="#2E7D32"
                :disabled="botaoFinalizarDesabilitado"
                :click="solicitarFinalizacaoOS"
            >
                Finalizar OS
            </BotaoTextoBase>

            <BotaoTextoBase
                v-if="podeExibirBotaoCancelar"
                color="#D32F2F"
                :disabled="props.disabled || dialogAcaoExecutando"
                :click="solicitarCancelamentoOS"
            >
                Cancelar OS
            </BotaoTextoBase>

            <BotaoTextoBase
                v-if="podeExibirBotaoReabrir"
                color="#EF6C00"
                :disabled="props.disabled || dialogAcaoExecutando"
                :click="solicitarReaberturaOS"
            >
                Reabrir OS
            </BotaoTextoBase>

            <BotaoTextoBase
                :disabled="disabledEditar"
                :click="editarOrdemDeServico"
            >
                Gravar
            </BotaoTextoBase>
        </template>

        <template #modal-conteudo>
            <div class="c-modal-conteudo">
                <ConteudoModal
                    ref="conteudoModalRef"
                    :disabled="props.disabled"
                    :dado="dado"
                    :jobs="props.jobs"
                    :itensDaOS="itensDaOS"
                    :coordenadores="props.coordenadores"
                    :ordensDeServico="props.ordensDeServico"
                    :historico="historico"
                    :podeEditarDatasEtapas="podeEditarDatasEtapas"
                    :canAddItemOS="canAddItemOSComputed"
                    :canEditItemOS="canEditItemOSComputed"
                    :itemEditOnlyStatusLC="coordenadorContratoSomenteLC"
                    :itemEditSemRestricaoStatus="usuarioEhGrupoSobressalentes"
                    :itemEditAllowedStatuses="statusPermitidosEdicaoItem"
                    @update:alteraDado="alteraDado"
                    @adicionarItem="adicionarItem"
                    @editarItem="editarItem"
                    @removerItem="removerItem"
                />

                <CartaoOSFilhas :osFilhas="osFilhas" />
            </div>

            <ModalItemOS
                :visible="modalItemOS"
                :item="itemSelecionado"
                :osQuantidade="dado.osQuantidade"
                :disabled="props.disabled || !canEditItemOSComputed"
                :podeEditarValorUnitario="podeEditarValorUnitarioItem"
                :podeEditarDataNecessidade="podeEditarDataNecessidadeItem"
                :somenteStatusLCParaCamposSensiveis="coordenadorContratoSomenteLC"
                :dataNecessidadeSomenteLeitura="dataNecessidadeSomenteLeituraItem"
                @close="fecharModalItem"
                @salvar="salvarItem"
            />

            <v-dialog v-model="dialogAcaoVisivel" max-width="560" persistent>
                <v-card>
                    <v-card-title class="text-h6">{{ dialogAcaoTitulo }}</v-card-title>
                    <v-card-text>{{ dialogAcaoMensagem }}</v-card-text>
                    <v-card-actions class="justify-end">
                        <v-btn text :disabled="dialogAcaoExecutando" @click="fecharDialogConfirmacao">Cancelar</v-btn>
                        <v-btn color="primary" :loading="dialogAcaoExecutando" :disabled="dialogAcaoExecutando" @click="confirmarDialogAcao">
                            {{ dialogAcaoBotaoConfirmar }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </template>
    </ModalFullscreen>
</template>

<style scoped>
.c-modal-conteudo {
    display: grid;
    gap: 8px;
    align-content: start;
}
</style>
